import Image from 'next/image';
import {ReactNode} from 'react';
import styled from 'styled-components';

/** Grouped list of menu rows, mirroring the mobile "Card large" menu panels. */
export const MenuCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 14px;
  box-shadow: ${(p) => p.theme.elevation.xs};
`;

export const MenuRowShell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 14px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  transition: background ${(p) => p.theme.motion.duration.fast}
    ${(p) => p.theme.motion.easing.out};

  /* The card keeps overflow visible for dropdowns, so rows clip themselves. */
  &:first-child {
    border-radius: 13px 13px 0 0;
  }

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 13px 13px;
  }

  &:only-child {
    border-radius: 13px;
  }

  &:hover {
    background: ${(p) => p.theme.colors.surfaceMuted};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const MenuRowIcon = styled.span<{$accent?: boolean; $plain?: boolean}>`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  color: ${(p) => p.theme.colors.violet600};
  background: ${(p) =>
    p.$accent ? p.theme.colors.secondary400 : p.theme.colors.secondary100};

  /* Non-accent: tint icons to violet600. Accent: force white on solid bubble.
     $plain: leave SVG/currentColor alone (glyph-only icons). */
  img,
  svg {
    filter: ${(p) =>
      p.$plain
        ? 'none'
        : p.$accent
        ? 'brightness(0) invert(1)'
        : 'brightness(0) saturate(100%) invert(48%) sepia(38%) saturate(968%) hue-rotate(214deg) brightness(92%) contrast(88%)'};
  }
`;

export const MenuRowLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.4;
  color: ${(p) => p.theme.colors.primary500};
`;

export const MenuRowValue = styled.span`
  flex-shrink: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${(p) => p.theme.colors.textMuted};
`;

export const MenuRowBadge = styled.span`
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 11px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.neutral100};
  background: ${(p) => p.theme.colors.violet600};
`;

const Chevron = styled.svg<{$open?: boolean}>`
  flex-shrink: 0;
  color: ${(p) => p.theme.colors.textMuted};
  transform: rotate(${(p) => (p.$open ? '90deg' : '0deg')});
  transition: transform ${(p) => p.theme.motion.duration.base}
    ${(p) => p.theme.motion.easing.out};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const MenuRowChevron = ({open}: {open?: boolean}) => (
  <Chevron
    $open={open}
    width="14"
    height="14"
    viewBox="0 0 11 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M9.75282 7.69821L2.02382 0.348215C1.80422 0.165516 1.53718 0.0490437 1.25388 0.012392C0.970575 -0.0242596 0.68269 0.0204182 0.423821 0.141214C0.226117 0.306406 0.0990906 0.540968 0.0687838 0.796812C0.0384769 1.05266 0.107183 1.3104 0.260821 1.51721L7.38182 8.28421L0.265821 15.0512C0.169673 15.1479 0.0965261 15.2649 0.0518093 15.3937C0.00709248 15.5225 -0.0080471 15.6598 0.00751495 15.7952C0.023077 15.9306 0.0689402 16.0608 0.141702 16.1761C0.214463 16.2914 0.312256 16.3889 0.427821 16.4612C0.679564 16.6178 0.978812 16.6796 1.27198 16.6356C1.56515 16.5916 1.83309 16.4447 2.02782 16.2212L9.75382 8.86122C9.8329 8.78637 9.89586 8.69618 9.93887 8.59615C9.98187 8.49613 10.004 8.38838 10.0039 8.2795C10.0038 8.17062 9.9815 8.06291 9.93832 7.96296C9.89515 7.86301 9.83203 7.77292 9.75282 7.69821Z"
      fill="currentColor"
    />
  </Chevron>
);

interface MenuRowProps {
  label: string;
  iconName: string;
  iconWidth?: number;
  iconHeight?: number;
  /** Highlights the icon bubble, e.g. for rows waiting for user action. */
  accent?: boolean;
  /**
   * Skip CSS color filter — for multi-color SVGs (e.g. white detail strokes)
   * that already look correct on the soft icon bubble.
   */
  plain?: boolean;
  badge?: string | number;
  value?: string;
  trailing?: ReactNode;
  onClick?: () => void;
}

export const MenuRow = ({
  label,
  iconName,
  iconWidth = 18,
  iconHeight = 18,
  accent,
  plain,
  badge,
  value,
  trailing,
  onClick,
}: MenuRowProps) => (
  <MenuRowShell role="button" tabIndex={0} onClick={onClick}>
    <MenuRowIcon $accent={accent} $plain={plain}>
      <Image
        src={`/icons/${iconName}.svg`}
        width={iconWidth}
        height={iconHeight}
        alt=""
      />
    </MenuRowIcon>
    <MenuRowLabel>{label}</MenuRowLabel>
    {value ? <MenuRowValue>{value}</MenuRowValue> : null}
    {badge !== undefined && badge !== '' ? (
      <MenuRowBadge>{badge}</MenuRowBadge>
    ) : null}
    {trailing ?? <MenuRowChevron />}
  </MenuRowShell>
);
